import {
  Entity,
  OrgWorker,
  Structure,
  Team,
  Unit,
} from "model/OrganizationalStructure";

const asNum = (v: any) => Number(v);
const keyOf = (w: any) => asNum(w?.workerId ?? w?.id);

const isWorkerInAnyTeam = (structure: Structure, workerIdNum: number) =>
  structure.entities.some((e) =>
    (e.units ?? []).some((u) =>
      (u.workGroups ?? []).some((wg) =>
        (wg.workers ?? []).some((w) => keyOf(w) === workerIdNum)
      )
    )
  );

const findWorkerAnywhere = (
  state: OrganizationalStructureState,
  workerIdNum: number
): OrgWorker | undefined => {
  for (const e of state.structure.entities) {
    for (const u of e.units ?? []) {
      for (const wg of u.workGroups ?? []) {
        const hit = (wg.workers ?? []).find((w) => keyOf(w) === workerIdNum);
        if (hit) return hit as OrgWorker;
      }
    }
  }
  return (state.unassignedWorkers ?? []).find((w) => keyOf(w) === workerIdNum);
};

const addToUnassignedIfMissing = (unassigned: OrgWorker[], worker: OrgWorker) =>
  unassigned.some((w) => keyOf(w) === keyOf(worker))
    ? unassigned
    : [...unassigned, worker];

export type OrganizationalStructureActions =
  | { type: "SET_IS_LOADING"; payload: boolean }
  | {
      type: "SET_REQUEST_ERROR";
      payload: OrganizationalStructureState["error"];
    }
  | {
      type: "GET_COMPANY_STRUCTURE";
      payload: Structure;
    }
  | {
      type: "GET_TEAM_MEMBERS";
      payload: { workGroupId: number; members: OrgWorker[] };
    }
  | {
      type: "UPDATE_TEAM_MEMBERS";
      payload: { workGroupId: number; worker: OrgWorker };
    }
  | {
      type: "CREATE_TEAM";
      payload: { companyUnitId: number; team: Team };
    }
  | {
      type: "CREATE_ENTITY";
      payload: { entity: Entity };
    }
  | {
      type: "CREATE_UNIT";
      payload: { companyEntityId: number; unit: Unit };
    }
  | {
      type: "DELETE_UNIT";
      payload: { companyEntityId: number; unitId: number };
    }
  | {
      type: "DELETE_TEAM";
      payload: { companyUnitId: number; workgroupId: number };
    }
  | {
      type: "DELETE_ENTITY";
      payload: { entityId: number };
    }
  | {
      type: "UPDATE_ENTITY";
      payload: { entityId: number; name: string };
    }
  | {
      type: "UPDATE_UNIT";
      payload: { entityId: number; unitId: number; name: string };
    }
  | {
      type: "UPDATE_TEAM";
      payload: {
        teamId: number;
        name: string;
        companyUnitId: number | undefined;
      };
    }
  | {
      type: "ADD_WORKER_TO_STRUCTURE";
      payload: OrgWorker;
    }
  | {
      type: "UPDATE_WORKER";
      payload: OrgWorker;
    }
  | {
      type: "DELETE_WORKER";
      payload: number;
    }
  | {
      type: "TOGGLE_TEAM";
      payload: number;
    }
  | {
      type: "UPDATE_TEAM_STATE_MEMBERS";
      payload: {
        teamId: number;
        members: OrgWorker[];
        currentPage: number;
        totalPages: number;
      };
    }
  | {
      type: "SET_TEAM_LOADING";
      payload: boolean;
    }
  | {
      type: "GET_UNASSIGNED_WORKERS";
      payload: OrgWorker[];
    }
  | {
      type: "UPDATE_TEAM_PAGINATION";
      payload: { teamId: number; page: number };
    }
  | {
      type: "UNASSIGN_WORKER_FROM_WORKGROUP";
      payload: { workerId: number; workGroupId: number };
    };

export type OrganizationalStructureState = {
  isLoading: boolean;
  error: { message: string; err: Error } | null;
  structure: {
    entities: Entity[];
  };
  members: { [workgroupId: number]: OrgWorker[] };
  openTeams: number[];
  teamMembers: Record<number, OrgWorker[]>;
  teamPagination: Record<number, { currentPage: number; totalPages: number }>;
  teamLoading: boolean;
  unassignedWorkers: OrgWorker[];
};

export const organizationalStructureInitState: OrganizationalStructureState = {
  isLoading: false,
  error: null,
  structure: {
    entities: [],
  },
  members: {},
  openTeams: [],
  teamMembers: {},
  teamPagination: {},
  teamLoading: false,
  unassignedWorkers: [],
};

export const organizationalStructureStateReducer = (
  state: OrganizationalStructureState,
  action: OrganizationalStructureActions
): OrganizationalStructureState => {
  switch (action.type) {
    case "GET_COMPANY_STRUCTURE": {
      return {
        ...state,
        structure: action.payload,
      };
    }

    case "GET_TEAM_MEMBERS": {
      const { workGroupId, members } = action.payload;
      return {
        ...state,
        members: {
          ...state.members,
          [workGroupId]: members,
        },
      };
    }

    case "UPDATE_TEAM_MEMBERS": {
      const { workGroupId, worker } = action.payload;
      return {
        ...state,
        teamMembers: {
          ...state.teamMembers,
          [workGroupId]: [...(state.teamMembers[workGroupId] || []), worker],
        },
      };
    }

    case "TOGGLE_TEAM": {
      const teamId = action.payload;
      const isOpen = state.openTeams.includes(teamId);

      return {
        ...state,
        openTeams: isOpen
          ? state.openTeams.filter((id) => id !== teamId)
          : [...state.openTeams, teamId],
      };
    }

    case "UPDATE_TEAM_STATE_MEMBERS": {
      const { teamId, members, currentPage, totalPages } = action.payload;
      const nextTeamMembers = {
        ...state.teamMembers,
        [teamId]: members,
      };

      const nextStructure: Structure = {
        ...state.structure,
        entities: state.structure.entities.map((entity) => ({
          ...entity,
          units: entity.units.map((unit) => ({
            ...unit,
            workGroups: unit.workGroups.map((wg) =>
              asNum(wg.id) === asNum(teamId) ? { ...wg, workers: members } : wg
            ),
          })),
        })),
      };

      const assignedIds = new Set(members.map((m) => keyOf(m)));
      const nextUnassigned = (state.unassignedWorkers ?? []).filter(
        (w) => !assignedIds.has(keyOf(w))
      );

      return {
        ...state,
        structure: nextStructure,
        teamMembers: nextTeamMembers,
        teamPagination: {
          ...state.teamPagination,
          [teamId]: { currentPage, totalPages },
        },
        unassignedWorkers: nextUnassigned,
      };
    }

    case "SET_TEAM_LOADING": {
      return {
        ...state,
        teamLoading: action.payload,
      };
    }

    case "CREATE_TEAM": {
      const { companyUnitId, team } = action.payload;

      return {
        ...state,
        structure: {
          ...state.structure,
          entities: state.structure.entities.map((entity) => {
            return {
              ...entity,
              units: entity.units.map((unit) => {
                if (unit.id === companyUnitId) {
                  const newWorkGroups = [
                    ...(unit.workGroups || []),
                    {
                      id: team.id,
                      name: team.name,
                      companyUnitId: companyUnitId,
                      workers: team.workers || [],
                    },
                  ];
                  return {
                    ...unit,
                    workGroups: newWorkGroups,
                  };
                }
                return unit;
              }),
            };
          }),
        },
      };
    }

    case "CREATE_ENTITY": {
      const { entity } = action.payload;
      return {
        ...state,
        structure: {
          ...state.structure,
          entities: [
            {
              name: entity.name,
              id: entity.id,
              companyId: entity.companyId,
              units: [],
            },
            ...state.structure.entities,
          ],
        },
      };
    }

    case "CREATE_UNIT": {
      const { companyEntityId, unit } = action.payload;

      return {
        ...state,
        structure: {
          ...state.structure,
          entities: state.structure.entities.map((entity) => {
            if (entity.id === companyEntityId) {
              const newUnits = [
                ...(entity.units || []),
                {
                  id: unit.id,
                  name: unit.name,
                  companyEntityId: unit.companyEntityId,
                  workGroups: [],
                },
              ];
              return {
                ...entity,
                units: newUnits,
              };
            }
            return entity;
          }),
        },
      };
    }

    case "DELETE_UNIT": {
      const { unitId } = action.payload;

      return {
        ...state,
        structure: {
          ...state.structure,
          entities: state.structure.entities.map((entity) => {
            if (entity.units && entity.units.length > 0) {
              const filteredUnits = entity.units.filter(
                (unit) => unit.id !== unitId
              );

              return {
                ...entity,
                units: filteredUnits,
              };
            }
            return entity;
          }),
        },
      };
    }

    case "DELETE_TEAM": {
      const { companyUnitId, workgroupId } = action.payload;

      return {
        ...state,
        structure: {
          ...state.structure,
          entities: state.structure.entities.map((entity) => {
            if (entity.units && entity.units.length > 0) {
              const updatedUnits = entity.units.map((unit) => {
                if (unit.id === companyUnitId) {
                  const filteredWorkGroups = unit.workGroups.filter(
                    (workgroup) => workgroup.id !== workgroupId
                  );

                  return {
                    ...unit,
                    workGroups: filteredWorkGroups,
                  };
                }
                return unit;
              });

              return {
                ...entity,
                units: updatedUnits,
              };
            }
            return entity;
          }),
        },
        openTeams: state.openTeams.filter((id) => id !== workgroupId),
        teamMembers: Object.fromEntries(
          Object.entries(state.teamMembers).filter(
            ([key]) => parseInt(key) !== workgroupId
          )
        ),
        teamPagination: Object.fromEntries(
          Object.entries(state.teamPagination).filter(
            ([key]) => parseInt(key) !== workgroupId
          )
        ),
      };
    }

    case "DELETE_ENTITY": {
      const { entityId } = action.payload;

      return {
        ...state,
        structure: {
          ...state.structure,
          entities: state.structure.entities.filter(
            (entity) => entity.id !== entityId
          ),
        },
      };
    }

    case "UPDATE_ENTITY": {
      const { entityId, name } = action.payload;

      return {
        ...state,
        structure: {
          ...state.structure,
          entities: state.structure.entities.map((entity) =>
            entity.id === entityId ? { ...entity, name: name } : entity
          ),
        },
      };
    }

    case "UPDATE_UNIT": {
      const { entityId, unitId, name } = action.payload;

      return {
        ...state,
        structure: {
          ...state.structure,
          entities: state.structure.entities.map((entity) =>
            entity.id === entityId
              ? {
                  ...entity,
                  units: entity.units.map((unit) =>
                    unit.id === unitId ? { ...unit, name: name } : unit
                  ),
                }
              : entity
          ),
        },
      };
    }

    case "UPDATE_TEAM": {
      const { teamId, name, companyUnitId } = action.payload;

      let foundTeam: any | null = null;
      let sourceUnitId: number | null = null;

      for (const entity of state.structure.entities) {
        for (const unit of entity.units) {
          const t = (unit.workGroups || []).find((wg: any) => wg.id === teamId);
          if (t) {
            foundTeam = t;
            sourceUnitId = unit.id;
            break;
          }
        }
        if (foundTeam) break;
      }

      if (!foundTeam) {
        return state;
      }

      if (companyUnitId == null || companyUnitId === sourceUnitId) {
        return {
          ...state,
          structure: {
            ...state.structure,
            entities: state.structure.entities.map((entity) => ({
              ...entity,
              units: entity.units.map((unit) => ({
                ...unit,
                workGroups: (unit.workGroups || []).map((team: any) =>
                  team.id === teamId ? { ...team, name } : team
                ),
              })),
            })),
          },
        };
      }
      return {
        ...state,
        structure: {
          ...state.structure,
          entities: state.structure.entities.map((entity) => ({
            ...entity,
            units: entity.units.map((unit) => {
              if (unit.id === sourceUnitId) {
                return {
                  ...unit,
                  workGroups: (unit.workGroups || []).filter(
                    (wg: any) => wg.id !== teamId
                  ),
                };
              }
              if (unit.id === companyUnitId) {
                const nextWGs = (unit.workGroups || []).some(
                  (wg: any) => wg.id === teamId
                )
                  ? (unit.workGroups || []).map((wg: any) =>
                      wg.id === teamId
                        ? { ...wg, ...foundTeam, name, companyUnitId }
                        : wg
                    )
                  : [
                      ...(unit.workGroups || []),
                      { ...foundTeam, name, companyUnitId },
                    ];

                return { ...unit, workGroups: nextWGs };
              }
              return unit;
            }),
          })),
        },
      };
    }

    case "ADD_WORKER_TO_STRUCTURE": {
      const worker = action.payload;
      const workGroupIds = worker.workGroups.map((group) => group.id);

      const nextStructure: Structure = {
        ...state.structure,
        entities: state.structure.entities.map((entity) => ({
          ...entity,
          units: entity.units.map((unit) => ({
            ...unit,
            workGroups: unit.workGroups.map((workGroup) => {
              if (workGroupIds.includes(workGroup.id)) {
                return {
                  ...workGroup,
                  workers: [...(workGroup.workers || []), worker],
                };
              }
              return workGroup;
            }),
          })),
        })),
      };

      const nextTeamMembers: Record<number, OrgWorker[]> = {
        ...state.teamMembers,
        ...workGroupIds.reduce((acc, groupId) => {
          if (state.openTeams.includes(groupId)) {
            const currentMembers = state.teamMembers[groupId] || [];
            acc[groupId] = [...currentMembers, worker];
          }
          return acc;
        }, {} as Record<number, OrgWorker[]>),
      };

      const nextUnassigned = (state.unassignedWorkers ?? []).filter(
        (w) => keyOf(w) !== keyOf(worker)
      );

      return {
        ...state,
        structure: nextStructure,
        teamMembers: nextTeamMembers,
        unassignedWorkers: nextUnassigned,
      };
    }

    case "UPDATE_WORKER": {
      const worker = action.payload as OrgWorker;

      const nextIds = new Set<number>(
        (worker.workGroups || []).map((g: { id: number }) => asNum(g.id))
      );
      const updatedKey = keyOf(worker);

      const nextStructure: Structure = {
        ...state.structure,
        entities: state.structure.entities.map((entity) => ({
          ...entity,
          units: entity.units.map((unit) => ({
            ...unit,
            workGroups: unit.workGroups.map((wg) => {
              const inNew = nextIds.has(asNum(wg.id));
              const current = wg.workers ?? [];
              const exists = current.some((w: any) => keyOf(w) === updatedKey);

              let workers: OrgWorker[];
              if (inNew) {
                workers = exists
                  ? current.map((w: any) =>
                      keyOf(w) === updatedKey ? worker : w
                    )
                  : [...current, worker];
              } else {
                workers = current.filter((w: any) => keyOf(w) !== updatedKey);
              }
              return { ...wg, workers };
            }),
          })),
        })),
      };

      const nextTeamMembers: Record<number, OrgWorker[]> = {
        ...state.teamMembers,
        ...state.openTeams.reduce(
          (acc: Record<number, OrgWorker[]>, teamId) => {
            const inNew = nextIds.has(asNum(teamId));
            const current = state.teamMembers[teamId] || [];
            const exists = current.some((w) => keyOf(w) === updatedKey);

            let next: OrgWorker[];
            if (inNew) {
              next = exists
                ? current.map((w) => (keyOf(w) === updatedKey ? worker : w))
                : [...current, worker];
            } else {
              next = current.filter((w) => keyOf(w) !== updatedKey);
            }
            acc[teamId] = next;
            return acc;
          },
          {}
        ),
      };

      const stillAssigned = isWorkerInAnyTeam(nextStructure, updatedKey);
      const nextUnassigned = stillAssigned
        ? (state.unassignedWorkers ?? []).filter((w) => keyOf(w) !== updatedKey)
        : addToUnassignedIfMissing(state.unassignedWorkers ?? [], {
            ...worker,
            workGroups: worker.workGroups ?? [],
          });

      return {
        ...state,
        structure: nextStructure,
        teamMembers: nextTeamMembers,
        unassignedWorkers: nextUnassigned,
      };
    }

    case "DELETE_WORKER": {
      const workerId = action.payload;
      const targetKey = asNum(workerId);

      return {
        ...state,
        structure: {
          ...state.structure,
          entities: state.structure.entities.map((entity) => ({
            ...entity,
            units: entity.units.map((unit) => ({
              ...unit,
              workGroups: (unit.workGroups || []).map((workGroup) => ({
                ...workGroup,
                workers: (workGroup.workers || []).filter(
                  (existingWorker) => keyOf(existingWorker) !== targetKey
                ),
              })),
            })),
          })),
        },

        teamMembers: Object.fromEntries(
          Object.entries(state.teamMembers).map(([teamId, members]) => [
            teamId,
            members.filter((worker) => keyOf(worker) !== targetKey),
          ])
        ),

        unassignedWorkers: (state.unassignedWorkers ?? []).filter(
          (w) => keyOf(w) !== targetKey
        ),
      };
    }

    case "GET_UNASSIGNED_WORKERS": {
      return {
        ...state,
        unassignedWorkers: action.payload,
      };
    }

    case "UPDATE_TEAM_PAGINATION":
      return {
        ...state,
        teamPagination: {
          ...state.teamPagination,
          [action.payload.teamId]: {
            ...state.teamPagination[action.payload.teamId],
            currentPage: action.payload.page,
          },
        },
      };

    case "UNASSIGN_WORKER_FROM_WORKGROUP": {
      const { workerId, workGroupId } = action.payload;

      const targetId = asNum(workerId);
      const originalWorker = findWorkerAnywhere(state, targetId);

      const nextStructure: Structure = {
        ...state.structure,
        entities: state.structure.entities.map((entity) => ({
          ...entity,
          units: entity.units.map((unit) => ({
            ...unit,
            workGroups: (unit.workGroups || []).map((wg) => {
              if (asNum(wg.id) !== asNum(workGroupId)) return wg;
              const nextWorkers = (wg.workers || []).filter(
                (w: any) => keyOf(w) !== targetId
              );
              return { ...wg, workers: nextWorkers };
            }),
          })),
        })),
      };

      const nextTeamMembers = state.openTeams.includes(asNum(workGroupId))
        ? {
            ...state.teamMembers,
            [workGroupId]: (state.teamMembers[workGroupId] || []).filter(
              (w: any) => keyOf(w) !== targetId
            ),
          }
        : state.teamMembers;

      const stillAssigned = isWorkerInAnyTeam(nextStructure, targetId);
      const currentUnassigned = state.unassignedWorkers ?? [];

      const nextUnassigned = stillAssigned
        ? currentUnassigned.filter((w) => keyOf(w) !== targetId)
        : originalWorker
        ? addToUnassignedIfMissing(currentUnassigned, {
            ...originalWorker,
            workGroups: [],
          })
        : currentUnassigned;

      return {
        ...state,
        structure: nextStructure,
        teamMembers: nextTeamMembers,
        unassignedWorkers: nextUnassigned,
      };
    }

    case "SET_IS_LOADING": {
      return { ...state, isLoading: action.payload };
    }
    case "SET_REQUEST_ERROR": {
      return { ...state, error: action.payload };
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};
